import { setUnauthorizedHandler } from "@imi/api-client/client.js";
import { ClientApiProvider } from "@imi/api-client/components/ClientApiProvider.tsx";
import { byShortcut } from "@imi/ui/helpers/predicate.js";
import { useKeyboard } from "@imi/ui/hooks/useKeyboard.js";
import { isFunction } from "radashi";
import { use, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet } from "react-router";
import styles from "./App.module.css";
import { saveRedirectAfterSignin } from "./core/auth/callbacks";
import { signinSilentOnce } from "./core/auth/signinSilentOnce";
import { useAuth } from "./core/auth/useAuth";
import { EngineWorkerProvider } from "./core/engine/workers/contexts/EngineWorkerProvider";
import { RouteDialogsProvider } from "./core/routeDialogs/contexts/RouteDialogsProvider";
import { useAnalytics } from "./shared/components/Analytics/useAnalytics";
import { AppErrorFallback } from "./shared/components/ErrorBoundaries/AppErrorFallback";
import { FeatureFlagsContext } from "./shared/components/FeatureFlags/FeatureFlagsContext";
import { logger } from "./shared/helpers/logger";

export function App() {
  const auth = useAuth();
  const { tracker } = useAnalytics();
  const { signinSilent } = auth;
  const { isFeatureEnabled } = use(FeatureFlagsContext);
  const isAuthEnabled = isFeatureEnabled("authentication");

  useEffect(() => {
    if (!isAuthEnabled) return;
    setUnauthorizedHandler(async () => {
      const user = await signinSilentOnce(signinSilent);
      if (!user) {
        saveRedirectAfterSignin();
        window.location.href = "/auth/logout";
        return undefined;
      }
      return user.access_token;
    });
    return () => setUnauthorizedHandler(undefined);
  }, [isAuthEnabled, signinSilent]);

  useKeyboard(
    byShortcut("Escape"),
    () => {
      const { activeElement } = document;
      if (activeElement) {
        if ("blur" in activeElement && isFunction(activeElement.blur)) {
          activeElement.blur();
        }
      }
    },
    { preventDefault: false },
  );

  return (
    <ClientApiProvider
      authToken={auth.user?.access_token}
      baseURL={import.meta.env.VITE_API_BASE_URL}
    >
      <ErrorBoundary
        FallbackComponent={AppErrorFallback}
        onError={(error, info) => {
          logger.error("ErrorBoundary caught an error", { error, info });
          if (error instanceof Error) {
            tracker?.trackEvent("general-error", { error: error.message });
          }
        }}
      >
        <EngineWorkerProvider>
          <RouteDialogsProvider>
            <div className={styles.App} id="main-content">
              <Outlet />
            </div>
          </RouteDialogsProvider>
        </EngineWorkerProvider>
      </ErrorBoundary>
    </ClientApiProvider>
  );
}
